import { Accordion, Alert, Button, Dialog, DialogClose, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { getTransactionCategoryById } from '@/features/reports/reports.selectors'
import { useMemo, useEffect } from 'react'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { getRecategorizationExplanation } from './helpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles, TextColors } from '@/styles'
interface RecategorizationModalProps {
  oldCategoryId: number | null
  newCategoryId: number | null
  open: boolean
  close: () => void
  updateAction: () => void
}
export const RecategorizationModal = ({
  oldCategoryId,
  newCategoryId,
  open,
  close,
  updateAction,
}: RecategorizationModalProps) => {
  const oldCategory = useReselector(getTransactionCategoryById, oldCategoryId)
  const newCategory = useReselector(getTransactionCategoryById, newCategoryId)
  const financialProfile = useReselector(getFinancialProfile)
  const recategorizationExplainer = useMemo(() => {
    return getRecategorizationExplanation(
      oldCategory,
      newCategory,
      financialProfile?.taxEntityType
    )
  }, [oldCategory, newCategory, financialProfile?.taxEntityType])
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const track = useAnalyticsTrack()

  const shouldOpenModal = open && Boolean(recategorizationExplainer)

  useEffect(() => {
    if (open && !recategorizationExplainer) {
      updateAction()
      close()
    }
  }, [open, recategorizationExplainer, updateAction, close])

  const recategorize = () => {
    updateAction()
    track('clicked recategorize on recat confirm')
    close()
  }
  useEffect(() => {
    if (shouldOpenModal) {
      track('viewed recat confirm modal')
    }
  }, [shouldOpenModal, track])

  return (
    <Dialog open={shouldOpenModal} onOpenChange={(open) => !open && close()}>
      <Dialog.Content className="max-w-sm sm:max-w-md">
        <Dialog.Title>
          Are you sure you want to recategorize this as{' '}
          {newCategory?.name || 'Unselected'}?
        </Dialog.Title>

        <Dialog.Description>
          <div className="space-y-4 mt-2">
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              This transaction must be categorized in a particular way to be
              tax-compliant.
            </Text>

            <Alert>{recategorizationExplainer?.reason}</Alert>
            <Accordion variant="text" type="single" collapsible>
              <Accordion.Item value="item-1">
                <Accordion.Trigger>
                  Why is it important to use this category correctly?
                </Accordion.Trigger>
                <Accordion.Content>
                  {recategorizationExplainer?.importance}
                </Accordion.Content>
              </Accordion.Item>
            </Accordion>
          </div>
        </Dialog.Description>

        <Dialog.Footer
          className={
            isMobile ? 'flex flex-col-reverse gap-2 mt-6' : 'justify-end mt-6'
          }
        >
          <Button
            variant="link"
            onClick={recategorize}
            className={isMobile ? 'order-1 w-full' : ''}
          >
            Recategorize
          </Button>
          <DialogClose asChild>
            <Button variant="primary" className={isMobile ? 'w-full' : ''}>
              Cancel
            </Button>
          </DialogClose>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
